import { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import apiManager from '../../../lib/api';
import { motion } from 'framer-motion';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { T, useTolgee } from '@tolgee/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Interfaces para estadísticas
interface DashboardStats {
  totalUsers: number;
  totalProducts: number;
  totalOrders: number;
  totalSales: number;
  newUsers: number;
  pendingOrders: number;
  stockAlerts: number;
  orderStatusCounts: Record<string, { count: number; revenue: number }>;
  orderGrowth: number;
  revenueGrowth: number;
  dailySales: Array<{
    _id: string;
    orders: number;
    revenue: number;
    averageOrder: number;
  }>;
  paymentMethods: Array<{
    _id: string;
    count: number;
    revenue: number;
    averageOrderValue: number;
  }>;
  recentOrders: {
    id: string;
    customer: string;
    date: string;
    total: number;
    status: string;
    paymentMethod?: string;
    paymentStatus?: string;
  }[];
}

const AdminDashboard = () => {
  const { t } = useTolgee(['language']);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Obtener estadísticas generales
        const usersResponse = await apiManager.users.getUserStats();
        const productsResponse = await apiManager.products.getProductStats();
        const ordersResponse = await apiManager.orders.getOrderStats();
        
        if (usersResponse.success && productsResponse.success && ordersResponse.success) {
          // Combinar datos de las respuestas
          setStats({
            totalUsers: usersResponse.data.totalUsers || 0,
            totalProducts: productsResponse.data.totalProducts || 0,
            totalOrders: ordersResponse.data.totalOrders || 0,
            totalSales: ordersResponse.data.totalSales || 0,
            newUsers: usersResponse.data.newUsers || 0,
            pendingOrders: ordersResponse.data.pendingOrders || 0,
            stockAlerts: productsResponse.data.lowStockCount || 0,
            orderStatusCounts: ordersResponse.data.statusCounts || {},
            orderGrowth: ordersResponse.data.comparison?.orderGrowth || 0,
            revenueGrowth: ordersResponse.data.comparison?.revenueGrowth || 0,
            dailySales: ordersResponse.data.dailySales || [],
            paymentMethods: ordersResponse.data.paymentMethods || [],
            recentOrders: ordersResponse.data.recentOrders || []
          });
        } else {
          setError('admin.dashboard.error_loading_stats');
        }
      } catch (err) {
        console.error('Error al cargar estadísticas del dashboard:', err);
        setError('admin.dashboard.error_loading_data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStats();
  }, []);
  
  // Variantes para animaciones
  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: 0.1 * i
      }
    })
  };
  
  // Preparar datos para gráficas
  const prepareSalesChartData = () => {
    if (!stats?.dailySales) return null;
    
    const labels = stats.dailySales.map(day => day._id);
    const revenue = stats.dailySales.map(day => day.revenue);
    const orders = stats.dailySales.map(day => day.orders);
    
    return {
      labels,
      datasets: [
        {
          label: 'admin.dashboard.sales',
          data: revenue,
          borderColor: 'rgba(99, 102, 241, 1)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          fill: true,
          tension: 0.3
        },
        {
          label: 'admin.dashboard.orders',
          data: orders,
          borderColor: 'rgba(79, 70, 229, 1)',
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          tension: 0.3
        }
      ]
    };
  };
  
  const preparePaymentMethodsChartData = () => {
    if (!stats?.paymentMethods || stats.paymentMethods.length === 0) return null;
    
    const labels = stats.paymentMethods.map(method => method._id);
    const data = stats.paymentMethods.map(method => method.revenue);
    
    // Colores para los diferentes métodos de pago
    const backgroundColor = [
      'rgba(99, 102, 241, 0.7)',
      'rgba(79, 70, 229, 0.7)',
      'rgba(16, 185, 129, 0.7)',
      'rgba(245, 158, 11, 0.7)',
      'rgba(239, 68, 68, 0.7)'
    ];
    
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderColor: backgroundColor.map(color => color.replace('0.7', '1')),
          borderWidth: 1
        }
      ]
    };
  };
  
  const prepareOrderStatusChartData = () => {
    if (!stats?.orderStatusCounts) return null;
    
    const statusColors: Record<string, string> = {
      pending: 'rgba(245, 158, 11, 0.7)',
      processing: 'rgba(59, 130, 246, 0.7)',
      shipped: 'rgba(99, 102, 241, 0.7)',
      delivered: 'rgba(16, 185, 129, 0.7)',
      cancelled: 'rgba(239, 68, 68, 0.7)',
      payment_failed: 'rgba(239, 68, 68, 0.7)',
      refunded: 'rgba(147, 51, 234, 0.7)',
    };
    
    const statuses = Object.keys(stats.orderStatusCounts);
    const counts = statuses.map(status => stats.orderStatusCounts[status].count);
    const colors = statuses.map(status => statusColors[status] || 'rgba(156, 163, 175, 0.7)');
    
    return {
      labels: statuses.map(status => status.toUpperCase()),
      datasets: [
        {
          data: counts,
          backgroundColor: colors,
          borderColor: colors.map(color => color.replace('0.7', '1')),
          borderWidth: 1
        }
      ]
    };
  };
  
  return (
    <AdminLayout title={t('admin.dashboard.title', 'Panel de Administración')}>
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-lg"
        >
          <p><T keyName={error} /></p>
        </motion.div>
      ) : stats ? (
        <div className="space-y-8">
          {/* Tarjetas de resumen */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div 
              custom={0} 
              variants={cardVariants} 
              initial="initial" 
              animate="animate"
              className="bg-white rounded-xl shadow-sm border border-gray-200"
            >
              <div className="p-6">
                <div className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900"><T keyName="admin.dashboard.users" /></h3>
                <p className="text-2xl font-semibold mt-2">{stats.totalUsers}</p>
                {stats.newUsers > 0 && (
                  <p className="text-sm text-green-600 mt-1"><T keyName="admin.dashboard.new_users_week" params={{ count: stats.newUsers.toString() }} /></p>
                )}
              </div>
            </motion.div>
            
            <motion.div 
              custom={1} 
              variants={cardVariants} 
              initial="initial" 
              animate="animate"
              className="bg-white rounded-xl shadow-sm border border-gray-200"
            >
              <div className="p-6">
                <div className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center bg-green-50 text-green-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900"><T keyName="admin.dashboard.products" /></h3>
                <p className="text-2xl font-semibold mt-2">{stats.totalProducts}</p>
                {stats.stockAlerts > 0 && (
                  <p className="text-sm text-red-600 mt-1"><T keyName="admin.dashboard.stock_alerts" params={{ count: stats.stockAlerts.toString() }} /></p>
                )}
              </div>
            </motion.div>
            
            <motion.div 
              custom={2} 
              variants={cardVariants} 
              initial="initial" 
              animate="animate"
              className="bg-white rounded-xl shadow-sm border border-gray-200"
            >
              <div className="p-6">
                <div className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center bg-yellow-50 text-yellow-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900"><T keyName="admin.dashboard.orders" /></h3>
                <p className="text-2xl font-semibold mt-2">{stats.totalOrders}</p>
                <div className="flex items-center mt-1">
                  <div className={`h-4 w-4 rounded-full mr-2 ${stats.orderGrowth >= 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <p className={`text-sm ${stats.orderGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <T keyName="admin.dashboard.vs_previous" params={{ percent: `${stats.orderGrowth >= 0 ? '+' : ''}${stats.orderGrowth.toFixed(1)}` }} />
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              custom={3} 
              variants={cardVariants} 
              initial="initial" 
              animate="animate"
              className="bg-white rounded-xl shadow-sm border border-gray-200"
            >
              <div className="p-6">
                <div className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center bg-purple-50 text-purple-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900"><T keyName="admin.dashboard.sales" /></h3>
                <p className="text-2xl font-semibold mt-2">{stats.totalSales.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                <div className="flex items-center mt-1">
                  <div className={`h-4 w-4 rounded-full mr-2 ${stats.revenueGrowth >= 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <p className={`text-sm ${stats.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <T keyName="admin.dashboard.vs_previous" params={{ percent: `${stats.revenueGrowth >= 0 ? '+' : ''}${stats.revenueGrowth.toFixed(1)}` }} />
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Gráficas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfica de ventas */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4"><T keyName="admin.dashboard.sales_trend" /></h2>
              {prepareSalesChartData() && (
                <div className="h-64">
                  <Line
                    data={prepareSalesChartData() || { labels: [], datasets: [] }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top',
                        },
                        tooltip: {
                          mode: 'index' as const,
                          intersect: false,
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                </div>
              )}
            </motion.div>
            
            {/* Distribución de estados */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4"><T keyName="admin.dashboard.order_status" /></h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="h-64">
                  {prepareOrderStatusChartData() && (
                    <Pie
                      data={prepareOrderStatusChartData() || { labels: [], datasets: [] }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'right' as const,
                          }
                        }
                      }}
                    />
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2"><T keyName="admin.dashboard.distribution" /></h3>
                  <div className="space-y-2">
                    {stats.orderStatusCounts && Object.entries(stats.orderStatusCounts).map(([status, data]) => (
                      <div key={status} className="flex justify-between">
                        <span className="text-sm capitalize">{status}</span>
                        <span className="text-sm font-medium"><T keyName="admin.dashboard.orders_label" params={{ count: data.count.toString() }} /></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Métodos de pago */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4"><T keyName="admin.dashboard.payment_methods" /></h2>
              {preparePaymentMethodsChartData() && (
                <div className="h-64">
                  <Bar
                    data={preparePaymentMethodsChartData() || { labels: [], datasets: [] }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context: any) {
                              return context.parsed.y.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value: any) {
                              return value.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              )}
            </motion.div>
          </div>
          
          {/* Pedidos recientes */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900"><T keyName="admin.dashboard.recent_orders" /></h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <T keyName="admin.dashboard.id" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <T keyName="admin.dashboard.customer" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <T keyName="admin.dashboard.date" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <T keyName="admin.dashboard.total" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <T keyName="admin.dashboard.status" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <T keyName="admin.dashboard.payment" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <T keyName="admin.dashboard.action" />
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.recentOrders.length > 0 ? (
                    stats.recentOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          #{order.id.slice(-6)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order.customer}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(order.date).toLocaleDateString('es-ES')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'shipped' ? 'bg-indigo-100 text-indigo-800' :
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {order.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.paymentMethod && (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              order.paymentStatus === 'completed' ? 'bg-green-100 text-green-800' :
                              order.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {order.paymentStatus?.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <a href={`/admin/orders/${order.id}`} className="text-black hover:text-gray-700 font-medium">
                            <T keyName="admin.dashboard.view_details" />
                          </a>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                        <T keyName="admin.dashboard.no_recent_orders" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <a href="/admin/orders" className="text-sm font-medium text-black hover:text-gray-700">
                <T keyName="admin.dashboard.view_all_orders" />
              </a>
            </div>
          </motion.div>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <p className="text-gray-500"><T keyName="admin.dashboard.no_data_available" /></p>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard; 